import React, { Component } from 'react';
import { Card, Skeleton } from 'antd';
import { Chart, Geom, Axis, Tooltip, Coord, Legend } from 'bizcharts';
import { DataSet } from '@antv/data-set';
import PropTypes from 'prop-types';

/** Matches y-axis label word-wrap (15 chars) so row height allows multi-line names. */
function estimateProductLabelLines(text) {
  if (!text || text.length <= 15) return 1;
  const words = text.split(' ');
  let lines = 1;
  let currentLine = '';
  words.forEach(word => {
    if ((currentLine + word).length > 15 && currentLine !== '') {
      lines += 1;
      currentLine = `${word} `;
    } else {
      currentLine += `${word} `;
    }
  });
  return lines;
}

/** Must match billingInvoice.js / InvoiceDashboard suffix for integration per-SKU stats. */
const INTEGRATION_PRODUCT_STATS_SUFFIX = ' (Integration)';

function catalogProductHasIntegrationParent(product) {
  return product && Array.isArray(product.categoryParent) && product.categoryParent.includes('integration');
}

class AdditionalProductGraph extends Component {
  static propTypes = {
    data: PropTypes.array.isRequired,
    products: PropTypes.array,
    height: PropTypes.number,
    title: PropTypes.string,
    loading: PropTypes.bool
  };
  static defaultProps = {
    height: 400,
    title: 'Top Products',
    loading: false
  };

  componentDidMount() {
    // Refer to this issue: https://github.com/alibaba/BizCharts/issues/279#issuecomment-400438180
    window.dispatchEvent(new Event('resize'));
    // Add a small delay to ensure chart is rendered properly
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }
  formatPrice = value => {
    if (value === null || value === undefined || isNaN(value)) return 'RM 0.00';
    const formattedValue = parseFloat(value).toFixed(2);
    const numberWithComma = formattedValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `RM ${numberWithComma}`;
  };

  render() {
    const { data, products, height, title, loading } = this.props;
    console.log(data, 1); // Transform data for additional products only
    const additionalProductsData = [];
    if (data && data.length > 0) {
      // Accumulate data across all months if multiple months exist
      let accumulatedData = {};

      if (data.length > 1) {
        // Initialize accumulator for multi-month data
        accumulatedData = {
          'Total HostProtect Billed': 0,
          'Total HostProtect Collected': 0
        };

        // Add dynamic product keys
        if (products && products.length > 0) {
          products.forEach(product => {
            if (product.name === 'API Integration') return;
            if (catalogProductHasIntegrationParent(product)) {
              accumulatedData[`Total ${product.name}${INTEGRATION_PRODUCT_STATS_SUFFIX} Billed`] = 0;
              accumulatedData[`Total ${product.name}${INTEGRATION_PRODUCT_STATS_SUFFIX} Collected`] = 0;
            } else {
              accumulatedData[`Total ${product.name} Billed`] = 0;
              accumulatedData[`Total ${product.name} Collected`] = 0;
            }
          });
        } // Accumulate values from all months
        data.forEach(monthData => {
          Object.keys(accumulatedData).forEach(key => {
            if (monthData[key] !== undefined && monthData[key] !== null) {
              accumulatedData[key] += monthData[key];

              // Round monetary values to 2 decimal places to avoid floating point precision issues
              if (key.includes('Billed') || key.includes('Collected')) {
                accumulatedData[key] = Math.round(accumulatedData[key] * 100) / 100;
              }
            }
          });
        });
      } else {
        // Single month - use existing logic
        accumulatedData = data[0] || {};
      }

      // Add HostProtect data (always show even if null/zero)
      const hostProtectBilled = accumulatedData['Total HostProtect Billed'] || 0;
      const hostProtectCollected = accumulatedData['Total HostProtect Collected'] || 0;

      additionalProductsData.push({
        product: 'HostProtect',
        'Total Billed': Number(hostProtectBilled) || 0,
        'Total Collected': Number(hostProtectCollected) || 0
      });

      // Add dynamic products from filteredProducts (always show even if null/zero)
      if (products && products.length > 0) {
        products.forEach(product => {
          if (product.name === 'API Integration') return;
          const statName = catalogProductHasIntegrationParent(product) ? `${product.name}${INTEGRATION_PRODUCT_STATS_SUFFIX}` : product.name;
          const billed = Number(accumulatedData[`Total ${statName} Billed`] || 0);
          const collected = Number(accumulatedData[`Total ${statName} Collected`] || 0);

          additionalProductsData.push({
            product: product.name,
            'Total Billed': billed || 0,
            'Total Collected': collected || 0
          });
        });
      }
    } else {
      // Even without API data, show the products with zero values
      additionalProductsData.push({
        product: 'HostProtect',
        'Total Billed': 0,
        'Total Collected': 0
      });

      // Add dynamic products with zero values
      if (products && products.length > 0) {
        products.forEach(product => {
          if (product.name === 'API Integration') return;
          additionalProductsData.push({
            product: product.name,
            'Total Billed': 0,
            'Total Collected': 0
          });
        });
      }
    }

    // Sort by highest total billed amount
    additionalProductsData.sort((a, b) => b['Total Billed'] - a['Total Billed']);

    // Get the sorted product names to preserve order in chart
    const sortedProductNames = additionalProductsData.map(item => item.product);
    // console.log(sortedProductNames, 2); // Create dataset for the chart
    const ds = new DataSet();
    const dv = ds.createView().source(additionalProductsData);
    dv.transform({
      type: 'fold',
      fields: ['Total Collected', 'Total Billed'], // Total Collected first to show Total Billed on top
      key: 'type',
      value: 'amount'
    });

    const categoryCount = additionalProductsData.length;
    const maxLabelLines = Math.max(1, ...additionalProductsData.map(row => estimateProductLabelLines(row.product)));
    const bandHeight = 48 + (maxLabelLines - 1) * 15;
    const axisLegendBuffer = 120;
    const minHeight = typeof height === 'number' && !Number.isNaN(height) ? height : 400;
    const chartHeight = Math.max(minHeight, categoryCount * bandHeight + axisLegendBuffer);

    return (
      <Card
        title={title}
        bordered={false}
        style={{
          marginBottom: '16px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          height: '100%'
        }}
        headStyle={{
          backgroundColor: '#fafafa',
          borderBottom: '1px solid #f0f0f0',
          fontWeight: 600
        }}
      >
        <Skeleton loading={loading} active paragraph={{ rows: 10 }}>
          {' '}
          <Chart
            height={chartHeight}
            data={dv}
            forceFit
            padding={[20, 40, 80, 150]} // Increase left padding for longer labels
            scale={{
              product: {
                type: 'cat',
                values: sortedProductNames.reverse()
              }
            }}
          >
            <Coord transpose />
            <Axis
              name="product"
              label={{
                textStyle: {
                  fontSize: '11',
                  fill: '#666'
                },
                formatter: text => {
                  // Split long text into multiple lines
                  if (text.length > 15) {
                    const words = text.split(' ');
                    const lines = [];
                    let currentLine = '';

                    words.forEach(word => {
                      if ((currentLine + word).length > 15 && currentLine !== '') {
                        lines.push(currentLine.trim());
                        currentLine = word + ' ';
                      } else {
                        currentLine += word + ' ';
                      }
                    });

                    if (currentLine.trim() !== '') {
                      lines.push(currentLine.trim());
                    }

                    return lines.join('\n');
                  }
                  return text;
                },
                offset: 10
              }}
              tickLine={null}
              line={null}
            />
            <Axis
              name="amount"
              label={{
                formatter: this.formatPrice,
                textStyle: {
                  fontSize: '12',
                  fill: '#666'
                }
              }}
            />{' '}
            <Tooltip />
            <Geom
              type="interval"
              position="product*amount"
              color={['type', ['#E97451', '#1890ff']]}
              adjust={[
                {
                  type: 'dodge',
                  marginRatio: 0.1
                }
              ]}
              style={{
                radius: [2, 2, 0, 0]
              }}
            />
          </Chart>
        </Skeleton>
      </Card>
    );
  }
}

export default AdditionalProductGraph;
